import React, { useMemo } from "react";
import EChart from "./EChart";
import { ECHART_TOOLTIP, ECHART_TOOLBOX, ECHART_COLORS } from "../../Utilities/echartsTheme";
import { formatNumber, formatRawNumber } from "../../Utilities/numbers";
import moment from "moment";

const EChartsDualAxisLine = ({ data, dateFrom, dateTo }) => {
  const hasData = Array.isArray(data) && data.length > 0;

  const { dates, orderCounts, deliveredCounts, revenues, conversionRates } = useMemo(() => {
    if (!hasData) return { dates: [], orderCounts: [], deliveredCounts: [], revenues: [], conversionRates: [] };

    const start = dateFrom ? moment(dateFrom) : moment.min(data.map((d) => moment(d.date)));
    const end = dateTo ? moment(dateTo) : moment.max(data.map((d) => moment(d.date)));
    const byDate = data.reduce((acc, row) => {
      const key = moment(row.date).format("YYYY-MM-DD");
      acc[key] = {
        count: Number(row.order_count) || 0,
        delivered: Number(row.delivered_count) || 0,
        revenue: Number(row.revenue) || 0,
      };
      return acc;
    }, {});

    const dList = [];
    const cList = [];
    const delList = [];
    const rList = [];
    const convList = [];
    let cursor = start.clone();
    while (cursor.isSameOrBefore(end, "day")) {
      const key = cursor.format("YYYY-MM-DD");
      const row = byDate[key];
      const count = row?.count || 0;
      const delivered = row?.delivered || 0;
      dList.push(key);
      cList.push(count);
      delList.push(delivered);
      rList.push(row?.revenue || 0);
      convList.push(count > 0 ? Number(((delivered / count) * 100).toFixed(1)) : 0);
      cursor.add(1, "day");
    }
    return { dates: dList, orderCounts: cList, deliveredCounts: delList, revenues: rList, conversionRates: convList };
  }, [data, dateFrom, dateTo, hasData]);

  const avgOrders = useMemo(() => {
    if (!orderCounts.length) return 0;
    return orderCounts.reduce((a, b) => a + b, 0) / orderCounts.length;
  }, [orderCounts]);

  const avgRevenue = useMemo(() => {
    if (!revenues.length) return 0;
    return revenues.reduce((a, b) => a + b, 0) / revenues.length;
  }, [revenues]);

  const avgConversion = useMemo(() => {
    const totalOrders = orderCounts.reduce((a, b) => a + b, 0);
    const totalDelivered = deliveredCounts.reduce((a, b) => a + b, 0);
    if (totalOrders === 0) return 0;
    return Number(((totalDelivered / totalOrders) * 100).toFixed(1));
  }, [orderCounts, deliveredCounts]);

  const option = useMemo(() => {
    if (!hasData) return {};

    const ordersMarkLine = avgOrders > 0 ? {
      symbol: "none",
      lineStyle: { type: "dashed", color: ECHART_COLORS.primary, width: 1.5 },
      label: {
        formatter: `Avg: ${formatNumber(avgOrders)}`,
        position: "insideEndTop",
        color: ECHART_COLORS.primary,
        fontSize: 11,
        fontWeight: 600,
      },
      data: [{ yAxis: avgOrders }],
    } : undefined;

    const revenueMarkLine = avgRevenue > 0 ? {
      symbol: "none",
      lineStyle: { type: "dashed", color: ECHART_COLORS.accent, width: 1.5 },
      label: {
        formatter: `Avg: Rs ${formatNumber(avgRevenue)}`,
        position: "insideEndBottom",
        color: ECHART_COLORS.accent,
        fontSize: 11,
        fontWeight: 600,
      },
      data: [{ yAxis: avgRevenue }],
    } : undefined;

    const conversionMarkLine = avgConversion > 0 ? {
      symbol: "none",
      lineStyle: { type: "dashed", color: ECHART_COLORS.success, width: 1.5 },
      label: {
        formatter: `Avg: ${avgConversion}%`,
        position: "insideEndTop",
        color: ECHART_COLORS.success,
        fontSize: 11,
        fontWeight: 600,
      },
      data: [{ yAxis: avgConversion }],
    } : undefined;

    return {
      tooltip: {
        ...ECHART_TOOLTIP,
        trigger: "axis",
        formatter: (params) => {
          const date = moment(params[0].axisValue).format("DD MMM YYYY");
          let html = `<div style="font-weight:600;margin-bottom:4px">${date}</div>`;
          params.forEach((p) => {
            let val;
            if (p.seriesName === "Revenue") val = `Rs ${formatRawNumber(p.value)}`;
            else if (p.seriesName === "Conversion %") val = `${formatRawNumber(p.value)}%`;
            else val = formatRawNumber(p.value);
            html += `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color}"></span>
              ${p.seriesName}: <strong>${val}</strong>
            </div>`;
          });
          return html;
        },
      },
      legend: {
        top: 0,
        right: 50,
        textStyle: { fontSize: 12, color: "#64748B" },
        itemWidth: 16,
        itemHeight: 3,
      },
      toolbox: {
        ...ECHART_TOOLBOX,
        feature: {
          ...ECHART_TOOLBOX.feature,
          dataZoom: { title: { zoom: "Zoom", back: "Reset" } },
        },
      },
      grid: { left: "3%", right: "4%", bottom: "15%", top: "12%", containLabel: true },
      xAxis: {
        type: "category",
        data: dates,
        boundaryGap: true,
        axisLabel: {
          formatter: (v) => moment(v).format("DD MMM"),
          hideOverlap: true,
        },
      },
      yAxis: [
        {
          type: "value",
          name: "Orders",
          nameTextStyle: { color: ECHART_COLORS.primaryLight, fontSize: 11 },
          minInterval: 1,
          axisLabel: { formatter: (v) => formatNumber(v), color: ECHART_COLORS.primaryLight },
          splitLine: { lineStyle: { color: "#F1F5F9" } },
        },
        {
          type: "value",
          name: "Revenue",
          nameTextStyle: { color: ECHART_COLORS.accent, fontSize: 11 },
          axisLabel: { formatter: (v) => formatNumber(v), color: ECHART_COLORS.accent },
          splitLine: { show: false },
        },
        {
          type: "value",
          name: "Conversion %",
          nameTextStyle: { color: ECHART_COLORS.success, fontSize: 11 },
          min: 0,
          max: 100,
          axisLabel: { formatter: (v) => `${v}%`, color: ECHART_COLORS.success },
          splitLine: { show: false },
        },
      ],
      dataZoom: [
        { type: "inside", start: 0, end: 100 },
        {
          type: "slider",
          start: 0,
          end: 100,
          height: 20,
          bottom: 5,
          borderColor: "#E2E8F0",
          fillerColor: "rgba(59,130,246,0.1)",
          handleStyle: { color: ECHART_COLORS.primaryLight },
          moveHandleSize: 7,
        },
      ],
      series: [
        {
          name: "Orders",
          type: "bar",
          yAxisIndex: 0,
          data: orderCounts,
          barWidth: "60%",
          itemStyle: {
            color: {
              type: "linear",
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: ECHART_COLORS.primaryLight },
                { offset: 1, color: "rgba(59,130,246,0.3)" },
              ],
            },
            borderRadius: [3, 3, 0, 0],
          },
          markLine: ordersMarkLine,
          emphasis: { focus: "series", itemStyle: { color: ECHART_COLORS.primary } },
          animationDuration: 600,
        },
        {
          name: "Delivered",
          type: "bar",
          yAxisIndex: 0,
          data: deliveredCounts,
          barWidth: "60%",
          itemStyle: {
            color: {
              type: "linear",
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: ECHART_COLORS.success },
                { offset: 1, color: "rgba(34,197,94,0.3)" },
              ],
            },
            borderRadius: [3, 3, 0, 0],
          },
          emphasis: { focus: "series", itemStyle: { color: ECHART_COLORS.success } },
          animationDuration: 600,
          animationDelay: 100,
        },
        {
          name: "Revenue",
          type: "line",
          yAxisIndex: 1,
          data: revenues,
          smooth: true,
          symbol: "circle",
          symbolSize: 5,
          showSymbol: false,
          lineStyle: { width: 2.5, color: ECHART_COLORS.accent },
          itemStyle: { color: ECHART_COLORS.accent },
          markLine: revenueMarkLine,
          emphasis: { focus: "series" },
          animationDuration: 800,
          animationDelay: 200,
        },
        {
          name: "Conversion %",
          type: "line",
          yAxisIndex: 2,
          data: conversionRates,
          smooth: true,
          symbol: "diamond",
          symbolSize: 6,
          showSymbol: false,
          lineStyle: { width: 2, color: ECHART_COLORS.success, type: "dashed" },
          itemStyle: { color: ECHART_COLORS.success },
          markLine: conversionMarkLine,
          emphasis: { focus: "series" },
          animationDuration: 800,
          animationDelay: 300,
        },
      ],
    };
  }, [dates, orderCounts, deliveredCounts, revenues, conversionRates, hasData, avgOrders, avgRevenue, avgConversion]);

  if (!hasData) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 text-muted">
        No data for the selected period
      </div>
    );
  }

  return <EChart option={option} />;
};

export default EChartsDualAxisLine;
